import type { IntlShape } from 'react-intl';

/**
 * The localised title for a fresh-food line.
 *
 * Shopify generates this title in German — "Frischfutter für Energiebedarf von
 * 474 kCal/Tag" — and it is the same sentence for every fresh product, differing
 * only in the kcal number. So it is generated copy rather than a product name,
 * and rebuilding it from the catalogue is what makes it follow the UI language.
 *
 * That is the opposite call from the recipe names (`Ringeltanz`, `Cowgenuss`) and
 * from `Pfotenpunkte`: those are coined brand names and stay German in every
 * locale. The distinction is whether the string is a name or a sentence.
 *
 * `product.calories.kcal` arrives as a string from some endpoints and a number
 * from others, which is why it is normalised here instead of at each call site.
 *
 * With no kcal the sentence cannot be rebuilt, so Shopify's own title is shown
 * unchanged — German, but accurate. Same trade-off as the reference labels: the
 * backend's value beats an empty slot.
 *
 * This deliberately does NOT cover shop items, bonus treats or supplements. Those
 * carry real product names ("Tipi Topping") that only Shopify can translate.
 */
export const freshProductTitle = (
  lineItem:
    | {
        productTitle?: string;
        product?: { calories?: { kcal?: string | number } } | null;
      }
    | null
    | undefined,
  intl: IntlShape,
): string => {
  const raw = lineItem?.product?.calories?.kcal;
  const kcal = typeof raw === 'string' ? parseInt(raw, 10) : raw;

  if (!kcal || !Number.isFinite(kcal)) {
    return lineItem?.productTitle ?? intl.formatMessage({ id: 'KCAL.FRESH_FOOD' });
  }

  return intl.formatMessage({ id: 'SUBSCRIPTION.FRESH_FOOD_FOR_KCAL' }, { kcal });
};
